import { print } from "graphql";

import {
  AvailableIntegrationFragment,
  AvailableIntegrationsDocument,
} from "@/apollo/types";
import { createStorageKey } from "@/utils/storage";

/**
 * Last-known integrations catalogue, kept so a reload can render the workspace
 * without waiting for `AvailableIntegrations` — a ~300ms request that otherwise
 * blocks every route under a workspace slug.
 *
 * One slot, not one per account: the catalogue serialises to ~190KB, so a user
 * with a dozen workspaces would fill the localStorage quota. Nearly every user
 * opens a single workspace, so one slot hits almost always; for the rest it
 * misses and the query blocks exactly as it did before.
 */
const STORAGE_KEY = createStorageKey("available-integrations");

type Snapshot = {
  shape: string;
  accountId: string;
  integrations: AvailableIntegrationFragment[];
};

/**
 * Identifies the selection set the snapshot was written against, so a change to
 * the fragment invalidates snapshots holding the old shape. Derived from the
 * query rather than from `RELEASE_ID`, which does not move for uncommitted work
 * — precisely when the fragment is being edited — and which would otherwise
 * discard a perfectly good snapshot on every unrelated deploy.
 *
 * The document itself rather than a digest of it: at ~950 characters against a
 * ~180KB payload it costs nothing to store, and an exact comparison cannot
 * collide two different shapes onto one key.
 */
let printedDocument: string | undefined;
function currentShapeId() {
  printedDocument ??= print(AvailableIntegrationsDocument);
  return printedDocument;
}

/**
 * Structural check only — enough that consumers can `map`/`filter` the result
 * without throwing. Validating every field of 222 integrations would spend the
 * main-thread time this snapshot exists to save, and would duplicate the
 * fragment; `currentShapeId` is what guards the fields.
 */
function isIntegrationList(
  value: unknown,
): value is AvailableIntegrationFragment[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as AvailableIntegrationFragment).id === "string",
    )
  );
}

function discard() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage is unavailable; nothing was readable to begin with.
  }
}

export function readIntegrationsSnapshot(
  accountId: string | null,
): AvailableIntegrationFragment[] | null {
  if (!accountId) return null;
  try {
    return read(accountId);
  } catch {
    // This runs from a `useMemo`, so it runs during render: anything thrown
    // here escapes the component and reaches the app-level error boundary,
    // replacing the whole app with the unknown-exception view over a cache the
    // app does not need. Failing to read one is always just a miss.
    return null;
  }
}

function read(accountId: string): AvailableIntegrationFragment[] | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  let snapshot: Partial<Snapshot> | null = null;
  try {
    snapshot = JSON.parse(raw);
  } catch {
    // Unparseable, and it would stay that way on every future boot.
    discard();
    return null;
  }

  if (
    snapshot === null ||
    typeof snapshot !== "object" ||
    snapshot.shape !== currentShapeId() ||
    !isIntegrationList(snapshot.integrations)
  ) {
    // Anything unusable is dropped rather than left to be re-read. A snapshot
    // that consumers cannot map over would otherwise break every subsequent
    // load, not just this one.
    discard();
    return null;
  }

  // A snapshot for another workspace is a plain miss: this load overwrites it.
  if (snapshot.accountId !== accountId) return null;

  return snapshot.integrations;
}

export function writeIntegrationsSnapshot(
  accountId: string,
  integrations: AvailableIntegrationFragment[],
) {
  const write = () => {
    try {
      const snapshot: Snapshot = {
        shape: currentShapeId(),
        accountId,
        integrations,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Quota exceeded, or storage blocked entirely. The snapshot is an
      // optimisation, never state — losing it just means the next load waits.
    }
  };

  // ~190KB to serialise and store, on a main thread that is still finishing the
  // boot this snapshot exists to speed up.
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(write);
  } else {
    setTimeout(write, 0);
  }
}
